import React, { useState, memo, useMemo, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAuth } from '@/shared/hooks/useAuth';
import { useAdminAuth } from '@/shared/hooks/useAdminAuth';
import { useTenant } from '@/shared/hooks/useTenant';
import Sidebar from './Sidebar';
import { debeVolverAlPanelAdmin } from './super-admin-fuera-de-admin';
import Header from './Header';
import OptimizedContent from './OptimizedContent';
import { cn } from '@/shared/utils/cn';
import { ChatbotWidget } from '@/features/chatbot';
import { AgentChatSidebar } from '@/features/agent-assistant/components/AgentChatSidebar';
import { puedeUsarAsistente } from '@/features/agent-assistant/utils/puede-usar-asistente';
import TrialActivatedModal from '@/features/security/components/organisms/TrialActivatedModal';
import { useTrialModal } from '@/features/security/hooks/useTrialModal';
// #1060 — pedido de aceptación legal (Ley 172-13). Va acá, en el shell
// autenticado, porque es el único punto por el que pasan todas las pantallas de
// usuario logueado. Colgarlo de una ruta concreta dejaría afuera a quien entra
// por deeplink, que es justo el caso de las inducciones de cliente.
import LegalConsentModal from '@/features/security/components/organisms/LegalConsentModal';
import { useLegalConsent } from '@/features/security/hooks/useLegalConsent';
import TrialExpiredBanner from './TrialExpiredBanner';
// gap-mock-mode-banner — BLOCKER legal/comercial (MODO DEMO when any
// payment/fiscal/comms provider lacks real credentials).
import MockModeBanner from './MockModeBanner';
import { desplazamientoPorAvisos, rellenoSuperiorDelContenido } from './avisos-superiores';
import { selectTrialInfo } from '@/features/billing/redux/trial-selectors';
import { volverDeLaSuplantacion } from '@/features/impersonation/utils/volver-de-la-suplantacion';
import { useEstaSuplantando } from '@/features/impersonation/hooks/useEstaSuplantando';
import { useDispatch } from 'react-redux';
import BarraDeSuplantacion from './BarraDeSuplantacion';

import { ImpersonationFlyout } from '@/features/impersonation/components/ImpersonationFlyout';
import { closeSelector } from '@/features/impersonation/redux/impersonation-slice';
// PB0-CAT4-52 (Multi-Site) — mount SelectedSiteProvider so SiteSwitcherDropdown
// and per-site reads have a single source of truth across the dashboard shell.
import { SelectedSiteProvider, debePedirSitios } from '@/features/multi-site';

export interface MainLayoutProps {
  children: React.ReactNode;
  showSidebar?: boolean;
  className?: string;
}

const MainLayout: React.FC<MainLayoutProps> = ({
  children,
  showSidebar = true,
  className,
}) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    // Cargar estado inicial desde localStorage
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('sidebar-collapsed');
      return saved ? JSON.parse(saved) : false;
    }
    return false;
  });
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  // Usar el hook de autenticación correcto según la ruta
  const authUser = useAuth();
  const adminUser = useAdminAuth();
  const dispatch = useDispatch();

  // #1060 — el estado legal lo decide el servidor, no una bandera local.
  const {
    debePedir: debePedirLegal,
    estado: estadoLegal,
    enviando: enviandoLegal,
    error: errorLegal,
    aceptar: aceptarLegal,
  } = useLegalConsent();
  const { isSelectorOpen } = useSelector((state: any) => state.impersonation);
  // GH#2768 — Redux solo sabe que se está suplantando hasta que la página se recarga (y entrar a una cuenta recarga la página): el claim del token
  // lo sigue diciendo después. Sin esto la barra de abajo nunca se ve y el super-admin queda atrapado en la cuenta del cliente.
  const estaSuplantando = useEstaSuplantando();

  // #619/#661 QA (CAC 2026-09-03) — ESTE es el botón real de "detener impersonación" (el de ImpersonationFlyout.tsx es código muerto: su botón
  // navega a /admin-recovery, ruta que nunca existió). Cualquier fix de sesión tiene que estar en volverDeLaSuplantacion para tener efecto real.
  // GH#2768: la lógica se movió allí (se prueba con la sessionService real). Va SIEMPRE a /admin/login: el inicio de la suplantación ya borró el
  // slot del admin (loginSuccess → clearSession(true), #1208), así que no hay sesión que restaurar; la razón completa está en volver-de-la-suplantacion.ts.
  const handleStopImpersonation = () => volverDeLaSuplantacion({ dispatch });


  // Seleccionar usuario según la ruta con fallback
  // En rutas admin: usar adminUser, pero si está vacío y authUser tiene rol de sistema
  // Y adminUser está autenticado (isAuthenticated), usar authUser como fallback
  const systemRoles = [
    'SUPER_ADMIN',
    'AGENCY_ADMIN',
    'PROJECT_DEVELOPER_ADMIN',
  ];
  const isAuthUserSystemRole =
    authUser.user && systemRoles.includes(authUser.user.role);

  // Solo usar fallback si adminUser está autenticado (significa que la sesión existe)
  // Si adminUser no está autenticado, no usar fallback para permitir que AuthGuard redirija
  const shouldUseFallback =
    isAuthUserSystemRole && authUser.isAuthenticated && !adminUser.user;

  const user = isAdminRoute
    ? adminUser.user || (shouldUseFallback ? authUser.user : null)
    : authUser.user;


  const { currentTenant } = useTenant();
  const trialInfo = useSelector(selectTrialInfo);

  // Hook para manejar el modal de trial (reemplaza la lógica anterior)
  // Pasar usuario para evitar duplicación de lógica
  const { isOpen: showTrialModal, hide: hideTrialModal } = useTrialModal(user);

  // Determinar si el banner de trial expirado debe mostrarse
  const showTrialBanner = useMemo(() => {
    if (user?.role === 'SUPER_ADMIN') return false;
    return trialInfo.trialEnabled && !trialInfo.isInTrial && trialInfo.daysRemaining === 0;
  }, [user?.role, trialInfo]);

  // Sincronizar estado del sidebar con localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(
        'sidebar-collapsed',
        JSON.stringify(sidebarCollapsed)
      );
    }
  }, [sidebarCollapsed]);

  // Memoizar funciones para evitar re-renders
  const toggleSidebar = useMemo(
    () => () => {
      setSidebarOpen(prev => !prev);
    },
    []
  );

  const toggleSidebarCollapsed = useMemo(
    () => () => {
      setSidebarCollapsed((prev: boolean) => !prev);
    },
    []
  );

  const closeSidebar = useMemo(
    () => () => {
      setSidebarOpen(false);
    },
    []
  );

  // Memoizar props del sidebar para evitar re-renders
  const sidebarProps = useMemo(
    () => ({
      user,
      tenant: currentTenant,
      onClose: closeSidebar,
    }),
    [user, currentTenant, closeSidebar]
  );

  // Memoizar props del header para evitar re-renders
  const headerProps = useMemo(
    () => ({
      onMenuClick: toggleSidebar,
      onDesktopMenuClick: toggleSidebarCollapsed,
      showMenuButton: showSidebar,
      user,
      tenant: currentTenant,
    }),
    [toggleSidebar, toggleSidebarCollapsed, showSidebar, user, currentTenant]
  );

  // GH#2765 (c): el SUPER_ADMIN guarda su sesión en el slot de /admin; fuera de /admin `user` sale null y el menú queda vacío con «Usuario».
  // Estas pantallas son de las cuentas de agencia: se le devuelve al panel de administración. (Impersonando, authUser.user existe: no entra aquí.)
  if (
    debeVolverAlPanelAdmin({
      isAdminRoute,
      hayUsuarioDeAgencia: !!authUser.user,
      adminAutenticado: !!adminUser.isAuthenticated,
      adminRole: adminUser.user?.role,
    })
  ) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  return (
    // GH#1201 — sin tenant (SUPER_ADMIN, panel /admin) no se pide la lista de sitios: el API contesta 403 en cada página.
    <SelectedSiteProvider enabled={debePedirSitios({ isAdminRoute, role: user?.role, tenantId: user?.tenantId })} tenantId={user?.tenantId}>
    <div className="min-h-screen bg-gray-50 uppercase-transitions">
      {/* Banner de Impersonación. GH#2768: va debajo del aviso amarillo y publica su alto (ver avisos-superiores.ts); ya no hay `pt-10` a mano. */}
      {estaSuplantando && (
        <BarraDeSuplantacion
          nombre={`${authUser.user?.firstName ?? ''} ${authUser.user?.lastName ?? ''}`.trim()}
          onVolver={handleStopImpersonation}
        />
      )}

      <div className="min-h-screen flex flex-col">
        {/* Sidebar para móviles */}
        {showSidebar && (
          <div
            className={cn(
              'fixed inset-0 z-40 lg:hidden',
              sidebarOpen ? 'block' : 'hidden'
            )}
          >
            <div className="fixed inset-0 bg-black/40" onClick={closeSidebar} />
            <div
              className="fixed bottom-0 left-0 flex w-64 flex-col bg-white"
              // GH#2318: debajo de los avisos fijos de arriba (prueba vencida y modo demo).
              style={{ top: desplazamientoPorAvisos(showTrialBanner) }}
            >
              <Sidebar {...sidebarProps} isMobile={true} />
            </div>
          </div>
        )}

        {/* Sidebar para desktop */}
        {showSidebar && (
          <div
            className={cn(
              'hidden lg:fixed lg:bottom-0 lg:flex lg:flex-col transition-all duration-300',
              sidebarCollapsed ? 'lg:w-16' : 'lg:w-64'
            )}
            // GH#2318: debajo de los avisos fijos de arriba (prueba vencida y modo demo).
            style={{ top: desplazamientoPorAvisos(showTrialBanner) }}
          >
            <Sidebar
              {...sidebarProps}
              onClose={() => { }}
              isMobile={false}
              collapsed={sidebarCollapsed}
            />
          </div>
        )}

        {/* gap-mock-mode-banner — Banner amarillo MODO DEMO (BLOCKER legal). */}
        <MockModeBanner />

        {/* Banner de trial expirado */}
        <TrialExpiredBanner />

        {/* Header fijo */}
        <div
          className={cn(
            'fixed z-30 transition-all duration-300',
            'left-0 right-0', // Ocupar todo el ancho en móviles
            showSidebar && (sidebarCollapsed ? 'lg:left-16' : 'lg:left-64')
          )}
          // GH#2318: la cabecera va DEBAJO de los avisos fijos (prueba vencida 40px + modo demo, que
          // publica su alto real); antes el aviso amarillo se le superponía y tapaba logo, menú, campana y avatar.
          style={{ top: desplazamientoPorAvisos(showTrialBanner) }}
        >
          <Header {...headerProps} />
        </div>

        {/* Contenido principal */}
        <div
          className={cn(
            'flex flex-1 flex-col transition-all duration-300',
            showSidebar && (sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-64')
          )}
          // Relleno superior: 64px (cabecera) + 40px (prueba vencida, si se ve) + el alto del aviso de modo demo.
          style={{ paddingTop: rellenoSuperiorDelContenido(showTrialBanner) }}
        >
          {/* Contenido de la página optimizado.
              NOTA (2026-07-11): NO sirve declarar aquí `flex flex-col` para que una
              página use `flex-1` y llene el alto: toda la cadena de ancestros usa
              `min-h-screen` (min-height, NO height) ⇒ su altura es `auto` y flex-1
              no reparte espacio (los hijos se dimensionan por contenido). Las páginas
              que necesitan alto completo (ej. Inbox) MIDEN su top real en runtime.
              Migrar el shell a `h-screen + overflow-y-auto` sería lo correcto, pero
              cambia el scroll de toda la app → hacerlo aparte y con test. */}
          <OptimizedContent className={cn('flex-1', className)}>
            {children}
          </OptimizedContent>
        </div>

        {/* Chatbot Widget - Solo visible para AGENCY_ADMIN */}
        <ChatbotWidget />

        {/* Asistente IA del sistema (OLA1 GHL #1) — pestaña lateral, self-contained */}
        {/* GH#2332: baja el panel bajo los avisos fijos (mismo cálculo que la cabecera) para que «Cerrar»,
            «Memorias» y «Limpiar» no queden debajo del aviso amarillo. */}
        {/* pmo #23486: el API del asistente solo deja pasar a 4 roles; al resto no se le monta el panel (antes veía el botón y `/memories` daba 403 tragado). */}
        {puedeUsarAsistente(user?.role) && <AgentChatSidebar topOffset={desplazamientoPorAvisos(showTrialBanner)} />}

        {/* Modal de prueba activada */}
        <TrialActivatedModal
          isOpen={showTrialModal}
          onClose={hideTrialModal}
        />

        {/* #1060 — aceptación legal. Sin salida hasta que el server confirme. */}
        <LegalConsentModal
          isOpen={debePedirLegal}
          estado={estadoLegal}
          enviando={enviandoLegal}
          error={errorLegal}
          onAceptar={aceptarLegal}
        />

        <ImpersonationFlyout
          isOpen={isSelectorOpen}
          onClose={() => dispatch(closeSelector())}
          collapsed={sidebarCollapsed}
        />
      </div>
    </div>
    </SelectedSiteProvider>
  );
};

// Optimización con React.memo para evitar re-renders innecesarios
const OptimizedMainLayout = memo(MainLayout);

OptimizedMainLayout.displayName = 'OptimizedMainLayout';

export default OptimizedMainLayout;
